import { isMultiCharSignalIcon } from '../../lib/signalIcon';
import { DEFAULT_SIGNAL_EMOJI } from './SignalEditorFields';
import type { FetchedSignal } from '../../api/dataApi';
import { REPORT_TYPE, type Template } from '../../types';

// Shared by PublicChannelPageScreen.tsx (live messages) and
// PublicReportScreen.tsx (a report's frozen checkpoint messages) - same
// text/emoji-splitting conventions as PublicChannelViewScreen.tsx's
// identical private helpers, duplicated rather than exported from there
// since that screen's helpers are tangled with its token/share-link
// state. Pure/presentational: no fetching, no store access.
const REPORT_EMOJI = '📝';

function publicDisplayText(message: FetchedSignal): string {
  if (!message.type) return message.text;
  const separatorIndex = message.text.lastIndexOf(' · ');
  return separatorIndex === -1 ? message.text : message.text.slice(0, separatorIndex);
}

function splitSignalEmoji(text: string): { emoji: string; label: string } {
  const spaceIndex = text.indexOf(' ');
  if (spaceIndex === -1) return { emoji: '', label: text };
  return { emoji: text.slice(0, spaceIndex), label: text.slice(spaceIndex + 1) };
}

export function medallionLabel(message: FetchedSignal): string {
  if (!message.type) return message.text;
  const text = publicDisplayText(message);
  return message.type === REPORT_TYPE ? text : splitSignalEmoji(text).label;
}

export function SignalMedallion({ message, templates }: { message: FetchedSignal; templates: Template[] }) {
  if (!message.type) return null;
  const isReport = message.type === REPORT_TYPE;
  const { emoji: textEmoji } = isReport ? { emoji: REPORT_EMOJI } : splitSignalEmoji(publicDisplayText(message));
  let templateEmoji: string | null = null;
  let colorHex: string | null = null;
  if (!isReport) {
    for (const t of templates) {
      const match = t.signals.find((s) => s.name === message.type);
      if (match) {
        templateEmoji = match.emoji;
        colorHex = match.colorHex;
        break;
      }
    }
  }
  const emoji = isReport ? REPORT_EMOJI : templateEmoji || textEmoji || DEFAULT_SIGNAL_EMOJI;
  const accent = colorHex ?? '#6B7280';
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 shrink-0 ${isMultiCharSignalIcon(emoji) ? 'text-xs font-semibold' : 'text-xl'}`}
      style={{ background: `${accent}1f`, borderColor: accent }}
    >
      {emoji}
    </div>
  );
}
